import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { 
  Grid,
  CardHeader,
  CardContent,
  Button,
  Typography,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { format } from 'date-fns'
import SearchIcon from '@material-ui/icons/Search'
import { useTranslation } from 'react-i18next'
import { CardNew, CardLogo } from '../styles/cards'
import { addPolicy } from '../redux/actions'
import { getCompanyLogo, EmblemLogo } from '../components/logo'
import { backendDateFormat } from '../dateFormat'
import DataFieldDate from '../datafields/dateField'
import DataFieldText from '../datafields/textField'
import SearchDropDown from '../datafields/searchField/searchDropDown'

// styles
const useStyles = makeStyles(theme => ({
  noPaddingField: {
    padding: 0,
  }
}))

// bordless input field
const noBordersStyle = makeStyles((theme) => ({
  root: {
    padding: 0,
    "& $notchedOutline": {
      border: "none",
    },
    "&:hover $notchedOutline": {
      border: "none",
    },
    "&$focused $notchedOutline": {
      border: "none",
    }
  },
  focused: {},
  notchedOutline: {},
}))


/**
 * A list of strings that represents possible possible search actions for policies.<br/>
 * Possible values:
 * * '_policy_' &ndash; search for a policy by the policy number and the effective date
 * * '_customer_' &ndash; search for a policy customer
 *
 * @const {string[]}
 * @category Policy
 */
const searchOptions = [
  "policy",
  "customer",
]


/**
 * It is a component that renders a title of the [new policy card]{@link NewPolicy}.
 * It provides functionality to switch between the available [search options]{@link searchOptions}
 * for policies. It is implemented via a _drop-down_ list which is a part of the card tile.
 *
 * @prop {object} props
 * @prop {string} props.value - The currently selected search option
 * @prop {array} props.options - The list of the available search options
 * @prop {function} props.onChange
 * Callback that changes the value of state [searchFor]{@link NewPolicy~searchFor}
 *
 * @memberOf NewPolicy
 */
function NewPolicyHeader(props) {
  const {t} = useTranslation('policy')
  const classes = useStyles()
  const noBorders = noBordersStyle()

  const handleChange = (event) => {
    props.onChange(event.target.value)
  }

  return (
    <Grid container spacing={1}>
      <Grid item>
        <Typography
          component="p"
          variant="h5"
        >
          {t('policy:find')}
        </Typography>
      </Grid>
      <Grid item>
        <FormControl
          variant="outlined"
          size="small"
        >
          <Select
            classes={{root: classes.noPaddingField}}
            value={props.value}
            onChange={handleChange}
            renderValue={option => (
              <Typography
                component="p"
                variant="h5"
              >
                {t(option)/*capitalizeFirstChar(option)*/}
              </Typography>
            )}
            input={
              <OutlinedInput
                classes={noBorders}
              />
            }
          >
            {props.options.map((option, index) => (
              <MenuItem key={index} value={option}>
                {t(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  )
}


/**
 * UI component which searches for a policy or a customer on the back-end and
 * adds its instance to _redux_ state [policies]{@link module:State~policies}.
 *
 * @component
 * @category Policy
 */
function NewPolicy(props) {
  const {t} = useTranslation('policy')

  /**
   * The initial values of the search creteria.
   *
   * @prop {string} number=''
   * policy number
   * @prop {string} date=today
   * policy effective date
   * @prop {string} customer=''
   * search string for customers
   */
  const initValues = {
    number: '',
    date: format(new Date(), backendDateFormat),
    customer: '',
  }

  /**
   * @typedef {object} state
   * @ignore
   */
  /**
   * State<br/>
   * The current values of the search creteria.
   *
   * @name values
   * @default [initValues]{@link NewPolicy~initValues}
   * @prop {object} values - state
   * @prop {function} setValues - setter
   * @type {state}
   * @memberOf NewPolicy
   * @inner
   */
  const [values, setValues] = React.useState(initValues)

  /**
   * State<br/>
   * A string that represents the target of the search.<br/>
   * The possible values are listed in {@link searchOptions}.
   *
   * @name searchFor
   * @default 'policy'
   * @prop {string} searchFor - state
   * @prop {function} setSearchFor - setter
   * @type {state}
   * @memberOf NewPolicy
   * @inner
   */
  const [searchFor, setSearchFor] = React.useState(searchOptions[0])

  
  /**
   * Callback<br/>
   * Pushed to card's input fields to update the [search creteria]{@link NewPolicy~values}.
   */
  const handleInputChange = (newValues) => {
    setValues(preValues => ({
      ...preValues,
      ...newValues,
    }))
  }

  /**
   * Method<br/>
   * Validates if all input values of the policy search form are set.
   *
   * @function
   * @returns {bool} 
   */
  const validateForm = () => {
    if (searchFor === "policy" && values.number && values.date) {
      return true
    }

    return false
  }


  /**
   * Event Handler<br/>
   * **_Event:_** click search button of the policy search form.<br/>
   * **_Implementation:_** pushes the inputed number and effective date for a policy
   * to prop [addPolicy]{@link NewPolicy} with request status _waiting_.
   * Then sets [initValues]{@link NewPolicy.initValues} to state [values]{@link NewPolicy~values}
   * to enable a new round of seraching for a policy or a customer.
   *
   * @see [MapPolicyCard]{@link PolicyView.MapPolicyCard} for possible request status of a policy instance
   */
  const handleSubmit = () => {
    // add policy card
    props.addPolicy({
      request_state: "waiting",
      policy_number: values.number,
      effective_date: values.date,
    })

    // update state to default
    setValues(initValues)
  }

  /**
   * Event Handler<br/>
   * **_Event:_** select an item from _drop-down list_ of the available customers.<br/>
   * **_Implementation:_** pushes the selected customer instance
   * to prop [addPolicy]{@link NewPolicy} with request status _customer_.
   *
   * @see [MapPolicyCard]{@link PolicyView.MapPolicyCard} for possible request status of a policy instance
   */
  const handleCustomerSelect = (value) => {

    // add customer card
    props.addPolicy({
      request_state: "customer",
      policies: [],
      ...value,
    })
  }

  return(
    <CardNew>
      <div style={{display: 'flex', flexDirection: 'column', flex: "1"}}>
        <CardHeader
          title={
            <NewPolicyHeader
              value={searchFor}
              options={searchOptions}
              onChange={setSearchFor}
            />
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            {searchFor === "policy" ? (
              <React.Fragment>
                <Grid item xs={11} md={4}>
                  <DataFieldText
                    id="policy-number"
                    value={values.number}
                    data={{
                      name: "number",
                      brief: t("policy.number")
                    }}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={11} md={4}>
                  <DataFieldDate
                    id="policy-date"
                    value={values.date}
                    data={{
                      name: "date",
                      brief: t("effective.date")
                    }}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={11} md={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={<SearchIcon />}
                    onClick={handleSubmit}
                    disabled={!validateForm()}
                  >
                    {t("find")}
                  </Button>
                </Grid>
              </React.Fragment>
            ) : (
              <Grid item xs={11}>
                <SearchDropDown
                  value={values.customer}
                  data={{
                    name: "customer",
                    brief: t("customer"),
                    endpoint: "partner",
                  }}
                  onChange={handleCustomerSelect}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </div>
      <div style={{width: 120}}>
      <CardLogo>
        <EmblemLogo
          logo={getCompanyLogo(props.user.company.attributes, "policy")}
          target="policy"
          size={170}
        />
      </CardLogo>
      </div>
    </CardNew>
  )
}

NewPolicy.propTypes = {
  /**
   * Redux state [user]{@link module:State~user}
   */
  user: PropTypes.object,
  /**
   * Redux action [addPolicy]{@link module:Actions.addPolicy}
   */
  addPolicy: PropTypes.func,
}


// connect to redux store
const mapStateToProps = (state) => ({
  user: state.user,
})

const mapDispatchToProps = {
  addPolicy: addPolicy,
}

export default connect(mapStateToProps, mapDispatchToProps)(NewPolicy)
